import { startOfDay, differenceInDays } from 'date-fns';
import memoizeOne from 'memoize-one';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import type { ValueDecoration } from '@atlassian/jira-polaris-domain-field/src/decoration/types.tsx';
import type { IntervalValue } from '@atlassian/jira-polaris-lib-date-time/src/types.tsx';
import { applyOptionLogic } from './logic/option/applyOptionLogic.tsx';
import {
	applyDateTimeIntervalLogic,
	applyDateTimeLogic,
} from './logic/datetime/applyDateTimeIntervalLogic.tsx';
import { applyNumberLogic } from './logic/number/createDefaultLogic.tsx';

/**
 * For selects and labels fields.
 */
export const getAppliedDecoration = memoizeOne(
	(decorations?: ValueDecoration[], value?: string): ValueDecoration | undefined => {
		if (decorations !== undefined && value !== undefined) {
			return decorations.find((decoration: ValueDecoration) =>
				applyOptionLogic(decoration.logic, value),
			);
		}
		return undefined;
	},
);

/**
 * For selects and labels fields.
 */
export const getAppliedDecorations = memoizeOne(
	(decorations: ValueDecoration[], values: string[]) => {
		const decorationMap: Record<string, ValueDecoration | undefined> = {};

		for (const value of values) {
			decorationMap[value] = getAppliedDecoration(decorations, value);
		}

		return decorationMap;
	},
);

export const getAppliedNumberDecoration = (
	decorations?: ValueDecoration[],
	value?: number,
): ValueDecoration | undefined => {
	if (decorations !== undefined && value !== undefined) {
		return decorations.find((decoration: ValueDecoration) =>
			applyNumberLogic(decoration.logic, value),
		);
	}
	return undefined;
};

export const getAppliedDateDecoration = memoizeOne(
	(
		decorations: ValueDecoration[] | undefined,
		value: string | undefined,
		locale: Locale,
	): ValueDecoration | undefined => {
		if (decorations !== undefined && value !== undefined) {
			const given = startOfDay(
				new Date(formatWithLocale(new Date(value), 'dd MMMM yyyy HH:mm', locale)),
			);
			const current = startOfDay(
				new Date(formatWithLocale(new Date(), 'dd MMMM yyyy HH:mm', locale)),
			);
			const diffDays = differenceInDays(given, current);

			return decorations.find((decoration: ValueDecoration) =>
				applyDateTimeLogic(decoration.logic, diffDays),
			);
		}
		return undefined;
	},
);

export const getAppliedIntervalDecoration = memoizeOne(
	(
		decorations: ValueDecoration[] | undefined,
		value: IntervalValue | undefined,
		locale: Locale,
	): ValueDecoration | undefined => {
		if (decorations !== undefined && value !== undefined) {
			const startDate = startOfDay(
				new Date(formatWithLocale(new Date(value?.start), 'dd MMMM yyyy HH:mm', locale)),
			);
			const endDate = startOfDay(
				new Date(formatWithLocale(new Date(value?.end), 'dd MMMM yyyy HH:mm', locale)),
			);
			const current = startOfDay(
				new Date(formatWithLocale(new Date(), 'dd MMMM yyyy HH:mm', locale)),
			);
			const diffDaysBeforeStart = differenceInDays(startDate, current);
			const diffDaysBeforeEnd = differenceInDays(endDate, current);

			return decorations.find((decoration: ValueDecoration) =>
				applyDateTimeIntervalLogic({
					logic: decoration.logic,
					diffDaysBeforeStart,
					diffDaysBeforeEnd,
				}),
			);
		}
		return undefined;
	},
);
